import { HttpClient } from "@/app/api/HttpClient"
import { API_PREFIX_LETAKY_PATH } from "@/constants/prefix-path-api"
import { FilterSearchParams } from "@/lib/external-components"
import { LetakRequest, LetakResponse } from "@/types/codegen/data-contracts"
import { RequestParams } from "@/types/codegen/http-client"
import { message } from "antd"

type LetakyListParams = Omit<FilterSearchParams, "populate"> & {
  fields?: string[]
  populate?: string | Record<string, any>
}

const LETAKY_LIST_FIELDS = ["title", "dateStart", "dateEnd", "locale", "publishedAt", "scheduledAt"]
const LETAKY_LIST_POPULATE = {
  localizations: {
    fields: ["locale"],
  },
}

// Dedupe in-flight list requests to avoid duplicate calls during mount/effect replay.
const inFlightLetakyListRequests = new Map<string, Promise<any>>()

const buildLetakyListRequestKey = (
  searchTerms: { [key: string]: string },
  publicationState: string,
): string => {
  const normalizedSearchTerms = Object.entries(searchTerms).sort(([a], [b]) => a.localeCompare(b))
  return JSON.stringify({ publicationState, searchTerms: normalizedSearchTerms })
}

export const fetchLetaky = async (
  searchTerms: { [key: string]: any } = {},
  publicationState: string = "preview",
) => {
  const params: LetakyListParams = {
    fields: LETAKY_LIST_FIELDS,
    populate: LETAKY_LIST_POPULATE,
    sort: "createdAt:desc",
    filters: {},
    pagination: {
      pageSize: "20",
    },
    publicationState,
  }

  const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page", "sort"]

  Object.keys(searchTerms).forEach(key => {
    const value = searchTerms[key]
    if (key === "populate") {
      params.populate = value
    } else if (key === "locale") {
      params.locale = value
    } else if (key === "publicationState") {
      // publicationState is passed as function arg, no need to treat as filter
      return
    } else if (key === "sort") {
      params.sort = value
    } else if (key === "domain") {
      if (value) {
        // Only filter on the relation that exists on the Letak content type (see Letak.websites in data-contracts).
        // Extra $or branches for legacy `website` / root `domain` make Strapi return 400 when those fields are absent.
        const letakyFilters = params.filters as Record<string, any>
        letakyFilters.websites = {
          domain: {
            $containsi: value,
          },
        }
      }
    } else if (!excludedKeys.includes(key)) {
      if (value) {
        params.filters[key] = { $containsi: value }
      }
    } else if (value) {
      params.pagination = {
        ...params.pagination,
        [key]: value,
      }
    }
  })

  const requestKey = buildLetakyListRequestKey(searchTerms, publicationState)
  const inFlightRequest = inFlightLetakyListRequests.get(requestKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  const requestPromise = (async () => {
    try {
      return await HttpClient.get(`${API_PREFIX_LETAKY_PATH}`, { params,skipDomainFilter:true })
    } catch (error) {
      message.error("Không thể lấy danh sách letaky. Vui lòng thử lại.")
      throw error
    } finally {
      inFlightLetakyListRequests.delete(requestKey)
    }
  })()

  inFlightLetakyListRequests.set(requestKey, requestPromise)
  return requestPromise
}

export const deleteLetaky = async (id: number) => {
  try {
    const response = await HttpClient.delete(`${API_PREFIX_LETAKY_PATH}/${id}`,{skipDomainFilter:true})
    return response
  } catch (error) {
    message.error("Không thể xóa letaky. Vui lòng thử lại.")
    throw error
  }
}
export const checkTitleLetaky = async (title: string) => {
  const params = {
    filters: {
      title: { $eq: title },
    },
    publicationState: "preview",
  };

  return await HttpClient.get(`${API_PREFIX_LETAKY_PATH}`, { params,skipDomainFilter:true });
};

export const createLetaky = async (data: LetakRequest) => {
  try {
    const response = await HttpClient.post(`${API_PREFIX_LETAKY_PATH}`, data,{skipDomainFilter:true})
    return response
  } catch (error) {
    message.error("Không thể tạo letaky. Vui lòng thử lại.")
    throw error
  }
}

export const updateLetaky = async (id: number, data: LetakRequest) => {
  try {
    const response = await HttpClient.put(`${API_PREFIX_LETAKY_PATH}/${id}`, data,{skipDomainFilter:true})
    return response
  } catch (error) {
    message.error("Không thể cập nhật letaky. Vui lòng thử lại.")
    throw error
  }
}

export interface LetakyVisibilityPayload {
  publishedAt?: string | null
  scheduledAt?: string | null
  unpublishAt?: string | null
}

// Chỉ ghi các mốc xuất bản (không gửi nội dung form) — dùng cho các thao tác
// Xuất bản / Lên lịch / Gỡ ở panel, độc lập với nút Cập nhật.
export const updateLetakyVisibility = async (id: number, data: LetakyVisibilityPayload) => {
  return await HttpClient.put(`${API_PREFIX_LETAKY_PATH}/${id}`, { data }, { skipDomainFilter: true })
}

export const getLetakyById = async (id: number, queryParams: RequestParams) => {
  try {
    const response = await HttpClient.get<LetakResponse>(`${API_PREFIX_LETAKY_PATH}/${id}`, { params: queryParams,skipDomainFilter:true })
    return response
  } catch (error) {
    message.error("Không thể lấy thông tin letaky. Vui lòng thử lại.")
    throw error
  }
}

export const createLocalization = async (id: number, data: LetakRequest) => {
  if (!id) {
    return null
  }

  try {
    const response = await HttpClient.post(`${API_PREFIX_LETAKY_PATH}/${id}/localizations`, data,{skipDomainFilter:true})
    return response
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error?.message || error.response?.data?.message || "Unable to create localization. Please try again"
    message.error(errorMessage)
    return null
  }
}
